import React, { useState, useEffect } from 'react';
import api, {
  fetchAllUsers,
  createUser,
  updateUser,
  deleteUser,
  fetchAllPapers,
  createPaper,
  updatePaper,
  deletePaper,
  fetchAdminIssues,
  createIssueWithPdf,
  updateIssueWithPdf,
  fetchContactForms,
  markContactFormAsRead,
  fetchNewsletterSubscriptions,
  fetchAdminEditorialBoard,
  createEditorialBoardMember,
  updateEditorialBoardMember,
  deleteEditorialBoardMember,
  fetchAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement
} from '../utils/api';
import { useAuth } from '../context/AuthContext';
import CitationModal from '../components/CitationModal';

const ComprehensiveAdminDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [papers, setPapers] = useState([]);
  const [issues, setIssues] = useState([]);
  const [contactForms, setContactForms] = useState([]);
  const [newsletterSubs, setNewsletterSubs] = useState([]);
  const [editorialBoard, setEditorialBoard] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCitationModal, setShowCitationModal] = useState(false);
  const [selectedPaperForCitation, setSelectedPaperForCitation] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const fetchIssues = async () => {
    try {
      const issuesData = await fetchAdminIssues();
      setIssues(Array.isArray(issuesData) ? issuesData : []);
    } catch (err) {
      console.error('Error loading issues:', err);
    }
  };

  const fetchEditorialBoard = async () => {
    try {
      const boardData = await fetchAdminEditorialBoard();
      setEditorialBoard(Array.isArray(boardData) ? boardData : []);
    } catch (err) {
      console.error('Error loading editorial board:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      switch (activeTab) {
        case 'users':
          const userData = await fetchAllUsers();
          setUsers(Array.isArray(userData) ? userData : []);
          break;
        case 'papers':
          const papersData = await fetchAllPapers();
          setPapers(Array.isArray(papersData) ? papersData : []);
          break;
        case 'issues':
          const [issuesData, papersForIssues] = await Promise.all([
            fetchAdminIssues(),
            fetchAllPapers()
          ]);
          setIssues(Array.isArray(issuesData) ? issuesData : []);
          setPapers(Array.isArray(papersForIssues) ? papersForIssues : []);
          break;
        case 'contact':
          const formsData = await fetchContactForms();
          setContactForms(Array.isArray(formsData) ? formsData : []);
          break;
        case 'newsletter':
          const subsData = await fetchNewsletterSubscriptions();
          setNewsletterSubs(Array.isArray(subsData) ? subsData : []);
          break;
        case 'editorial':
          const boardData = await fetchAdminEditorialBoard();
          setEditorialBoard(Array.isArray(boardData) ? boardData : []);
          break;
        case 'announcements':
          const announcementsData = await fetchAnnouncements();
          setAnnouncements(Array.isArray(announcementsData) ? announcementsData : []);
          break;
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setModalType('createUser');
    setSelectedItem(null);
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setModalType('editUser');
    setSelectedItem(user);
    setShowModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(userId);
        setUsers(users.filter(u => u.id !== userId));
        alert('User deleted successfully');
      } catch (err) {
        alert('Failed to delete user');
      }
    }
  };

  const handleCreatePaper = async () => {
    // Load issues if not already loaded
    if (issues.length === 0) {
      await fetchIssues();
    }
    setModalType('createPaper');
    setSelectedItem(null);
    setSelectedFile(null);
    setShowModal(true);
  };

  const handleEditPaper = async (paper) => {
    // Load issues if not already loaded
    if (issues.length === 0) {
      await fetchIssues();
    }
    setModalType('editPaper');
    setSelectedItem(paper);
    setSelectedFile(null);
    setShowModal(true);
  };

  const handleDeletePaper = async (paperId) => {
    if (window.confirm('Are you sure you want to delete this paper?')) {
      try {
        await deletePaper(paperId);
        setPapers(papers.filter(p => p.id !== paperId));
        alert('Paper deleted successfully');
      } catch (err) {
        alert('Failed to delete paper');
      }
    }
  };

  const handleShowCitation = (paper) => {
    setSelectedPaperForCitation(paper);
    setShowCitationModal(true);
  };

  const handleSaveUser = async (userData) => {
    try {
      if (modalType === 'createUser') {
        const newUser = await createUser(userData);
        setUsers([...users, newUser]);
        alert('User created successfully');
      } else {
        const updatedUser = await updateUser(selectedItem.id, userData);
        setUsers(users.map(u => u.id === selectedItem.id ? updatedUser : u));
        alert('User updated successfully');
      }
      setShowModal(false);
    } catch (err) {
      alert('Failed to save user');
    }
  };

  const handleSavePaper = async (paperData) => {
    try {
      // Add the manuscript file to the paperData
      if (selectedFile) {
        paperData.manuscript = selectedFile;
      }

      if (modalType === 'createPaper') {
        const newPaper = await createPaper(paperData);
        setPapers([...papers, newPaper]);
        alert('Paper created successfully');
      } else {
        const updatedPaper = await updatePaper(selectedItem.id, paperData);
        setPapers(papers.map(p => p.id === selectedItem.id ? updatedPaper : p));
        alert('Paper updated successfully');
      }
      setShowModal(false);
      setSelectedFile(null);
    } catch (err) {
      console.error('Error saving paper:', err);
      alert('Failed to save paper: ' + (err.response?.data?.message || err.message || 'Unknown error'));
    }
  };

  const UserModal = () => (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {modalType === 'createUser' ? 'Create New User' : 'Edit User'}
            </h5>
            <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
          </div>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            handleSaveUser(Object.fromEntries(formData));
          }}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">First Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="firstName"
                    defaultValue={selectedItem?.firstName}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="lastName"
                    defaultValue={selectedItem?.lastName}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    defaultValue={selectedItem?.email}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Role</label>
                  <select
                    className="form-control"
                    name="role"
                    defaultValue={selectedItem?.role || 'AUTHOR'}
                    required
                  >
                    <option value="AUTHOR">Author</option>
                    <option value="EDITOR">Editor</option>
                    <option value="ADMIN">Admin</option>
                    <option value="REVIEWER">Reviewer</option>
                  </select>
                </div>
              </div>
              {modalType === 'createUser' && (
                <div className="mb-3">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-control"
                    name="password"
                    required
                  />
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">Institution</label>
                <input
                  type="text"
                  className="form-control"
                  name="institution"
                  defaultValue={selectedItem?.institution}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {modalType === 'createUser' ? 'Create User' : 'Update User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const IssueModal = () => (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {modalType === 'createIssue' ? 'Create New Issue' : 'Edit Issue'}
            </h5>
            <button type="button" className="btn-close" onClick={() => {
              setShowModal(false);
              setSelectedFile(null);
            }}></button>
          </div>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const issueData = Object.fromEntries(formData);

            try {
              if (modalType === 'createIssue') {
                await createIssueWithPdf(issueData);
                alert('Issue created successfully');
              } else {
                await updateIssueWithPdf(selectedItem.id, issueData);
                alert('Issue updated successfully');
              }
              setShowModal(false);
              setSelectedFile(null);
              fetchIssues();
            } catch (err) {
              console.error('Error saving issue:', err);
              const errorMessage = err.response?.data?.detail || err.response?.data?.error || err.message || 'Unknown error';
              const validationErrors = err.response?.data?.validation;
              let fullErrorMessage = `Failed to save issue: ${errorMessage}`;
              if (validationErrors && validationErrors.length > 0) {
                fullErrorMessage += '\n\nValidation errors:\n' + validationErrors.map(e => `- ${e.field}: ${e.message}`).join('\n');
              }
              alert(fullErrorMessage);
            }
          }}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  defaultValue={selectedItem?.title}
                  required
                />
              </div>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Volume</label>
                  <input
                    type="number"
                    className="form-control"
                    name="volume"
                    defaultValue={selectedItem?.volume}
                    required
                    min="1"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Issue Number</label>
                  <input
                    type="number"
                    className="form-control"
                    name="issueNumber"
                    defaultValue={selectedItem?.issueNumber}
                    required
                    min="1"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Year</label>
                  <input
                    type="number"
                    className="form-control"
                    name="year"
                    defaultValue={selectedItem?.year || new Date().getFullYear()}
                    required
                    min="2000"
                    max="2100"
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Publication Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="publicationDate"
                  defaultValue={selectedItem?.publicationDate?.split('T')[0]}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Cover Image URL (optional)</label>
                <input
                  type="text"
                  className="form-control"
                  name="coverImage"
                  defaultValue={selectedItem?.coverImage}
                  placeholder="https://example.com/cover.jpg"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">PDF File (optional)</label>
                <input
                  type="file"
                  className="form-control"
                  name="pdfFile"
                  accept=".pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    setSelectedFile(file);
                  }}
                />
                {selectedFile && (
                  <div className="mt-2 text-success">
                    <i className="fas fa-check-circle me-2"></i>
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Status</label>
                <select
                  className="form-control"
                  name="isPublished"
                  defaultValue={selectedItem?.isPublished ? 'true' : 'false'}
                >
                  <option value="false">Draft</option>
                  <option value="true">Published</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => {
                setShowModal(false);
                setSelectedFile(null);
              }}>Cancel</button>
              <button type="submit" className="btn btn-primary">Save Issue</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const EditorialModal = () => (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {modalType === 'createEditorial' ? 'Add Editorial Board Member' : 'Edit Editorial Board Member'}
            </h5>
            <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
          </div>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const formData = new FormData(e.target);

            try {
              if (modalType === 'createEditorial') {
                await createEditorialBoardMember(formData);
                alert('Editorial board member added successfully');
              } else {
                await updateEditorialBoardMember(selectedItem.id, formData);
                alert('Editorial board member updated successfully');
              }
              setShowModal(false);
              fetchEditorialBoard();
            } catch (err) {
              alert('Failed to save editorial board member: ' + err.message);
            }
          }}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  defaultValue={selectedItem?.name}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Title/Position</label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  defaultValue={selectedItem?.title}
                  placeholder="e.g., Professor, Editor-in-Chief"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Affiliation</label>
                <input
                  type="text"
                  className="form-control"
                  name="affiliation"
                  defaultValue={selectedItem?.affiliation}
                  placeholder="e.g., Federal University Wukari"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Department</label>
                <input
                  type="text"
                  className="form-control"
                  name="department"
                  defaultValue={selectedItem?.department}
                  placeholder="e.g., Department of Agriculture"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  defaultValue={selectedItem?.email}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Bio/Expertise</label>
                <textarea
                  className="form-control"
                  name="bio"
                  rows="3"
                  defaultValue={selectedItem?.bio}
                  placeholder="Brief bio or areas of expertise"
                ></textarea>
              </div>
              <div className="mb-3">
                <label className="form-label">Photo</label>
                <input
                  type="file"
                  className="form-control"
                  name="photo"
                  accept="image/*"
                />
                {selectedItem?.photo && (
                  <small className="text-muted">Current photo will be kept if no new photo is uploaded</small>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Role</label>
                <select
                  className="form-control"
                  name="role"
                  defaultValue={selectedItem?.role || 'member'}
                >
                  <option value="chief_editor">Editor-in-Chief</option>
                  <option value="deputy_editor">Deputy Editor</option>
                  <option value="managing_editor">Managing Editor</option>
                  <option value="associate_editor">Associate Editor</option>
                  <option value="member">Board Member</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Display Order</label>
                <input
                  type="number"
                  className="form-control"
                  name="displayOrder"
                  defaultValue={selectedItem?.displayOrder || 999}
                  min="1"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Save Member</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const ContactModal = () => (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Contact Form Submission</h5>
            <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
          </div>
          <div className="modal-body">
            {selectedItem && (
              <div>
                <div className="mb-3">
                  <strong>Date:</strong> {new Date(selectedItem.createdAt).toLocaleString()}
                </div>
                <div className="mb-3">
                  <strong>Name:</strong> {selectedItem.name}
                </div>
                <div className="mb-3">
                  <strong>Email:</strong> <a href={`mailto:${selectedItem.email}`}>{selectedItem.email}</a>
                </div>
                <div className="mb-3">
                  <strong>Subject:</strong> {selectedItem.subject}
                </div>
                <div className="mb-3">
                  <strong>Message:</strong>
                  <div className="border p-3 rounded bg-light">
                    {selectedItem.message}
                  </div>
                </div>
                <div className="mb-3">
                  <strong>Status:</strong>
                  <span className={`badge ms-2 ${selectedItem.status === 'unread' ? 'bg-warning' : 'bg-success'}`}>
                    {selectedItem.status}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            {selectedItem?.status === 'unread' && (
              <button
                type="button"
                className="btn btn-success"
                onClick={async () => {
                  try {
                    await markContactFormAsRead(selectedItem.id);
                    const updatedForms = contactForms.map(f =>
                      f.id === selectedItem.id ? { ...f, status: 'read' } : f
                    );
                    setContactForms(updatedForms);
                    setSelectedItem({ ...selectedItem, status: 'read' });
                    alert('Marked as read');
                  } catch (err) {
                    alert('Failed to mark as read');
                  }
                }}
              >
                Mark as Read
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );

  const PaperModal = () => (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {modalType === 'createPaper' ? 'Create New Paper' : 'Edit Paper'}
            </h5>
            <button type="button" className="btn-close" onClick={() => {
              setShowModal(false);
              setSelectedFile(null);
            }}></button>
          </div>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const authors = formData.get('authors').split(',').map(a => ({ name: a.trim() }));
            formData.set('authors', JSON.stringify(authors));
            handleSavePaper(Object.fromEntries(formData));
          }}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    defaultValue={selectedItem?.title}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Authors (comma-separated)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="authors"
                    defaultValue={selectedItem?.authors?.map(a => typeof a === 'object' ? a.name : a).join(', ')}
                    placeholder="John Doe, Jane Smith"
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Category</label>
                  <select
                    className="form-control"
                    name="category"
                    defaultValue={selectedItem?.category}
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="Agricultural Economics">Agricultural Economics</option>
                    <option value="Crop Science">Crop Science</option>
                    <option value="Animal Science">Animal Science</option>
                    <option value="Soil Science">Soil Science</option>
                    <option value="Agricultural Engineering">Agricultural Engineering</option>
                    <option value="Food Science">Food Science</option>
                    <option value="Environmental Science">Environmental Science</option>
                    <option value="Plant Pathology">Plant Pathology</option>
                    <option value="Biotechnology">Biotechnology</option>
                    <option value="Forestry">Forestry</option>
                  </select>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Abstract</label>
                <textarea
                  className="form-control"
                  name="abstract"
                  rows="4"
                  defaultValue={selectedItem?.abstract}
                  required
                ></textarea>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Keywords</label>
                  <input
                    type="text"
                    className="form-control"
                    name="keywords"
                    defaultValue={selectedItem?.keywords}
                    placeholder="keyword1, keyword2, keyword3"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">DOI</label>
                  <input
                    type="text"
                    className="form-control"
                    name="doi"
                    defaultValue={selectedItem?.doi}
                    placeholder="10.33372/fuwjals.xxxx"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">Volume</label>
                  <input
                    type="number"
                    className="form-control"
                    name="volume"
                    defaultValue={selectedItem?.volume}
                    min="1"
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Issue</label>
                  <input
                    type="number"
                    className="form-control"
                    name="issue"
                    defaultValue={selectedItem?.issue}
                    min="1"
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Pages</label>
                  <input
                    type="text"
                    className="form-control"
                    name="pages"
                    defaultValue={selectedItem?.pages}
                    placeholder="1-10"
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Year</label>
                  <input
                    type="number"
                    className="form-control"
                    name="year"
                    defaultValue={selectedItem?.year || new Date().getFullYear()}
                    min="2000"
                    max="2100"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Issue</label>
                  <select
                    className="form-control"
                    name="issueId"
                    defaultValue={selectedItem?.issueId || ''}
                  >
                    <option value="">Not assigned</option>
                    {issues.map(issue => (
                      <option key={issue.id} value={issue.id}>
                        Vol {issue.volume}, Issue {issue.issue} ({issue.year})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-control"
                    name="status"
                    defaultValue={selectedItem?.status || 'SUBMITTED'}
                    required
                  >
                    <option value="SUBMITTED">Submitted</option>
                    <option value="UNDER_REVIEW">Under Review</option>
                    <option value="ACCEPTED">Accepted</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="REVISIONS_REQUIRED">Revisions Required</option>
                    <option value="PUBLISHED">Published</option>
                  </select>
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Manuscript File</label>
                  <input
                    type="file"
                    className="form-control"
                    name="manuscript"
                    accept=".pdf"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setSelectedFile(file);
                    }}
                  />
                  {selectedFile && (
                    <div className="mt-2 text-success">
                      <i className="fas fa-check-circle me-2"></i>
                      Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                  )}
                  {!selectedFile && selectedItem?.manuscriptUrl && (
                    <small className="text-muted">
                      <i className="fas fa-file-pdf me-1"></i>
                      Current file: {selectedItem.manuscriptUrl.split('/').pop()}
                    </small>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => {
                setShowModal(false);
                setSelectedFile(null);
              }}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {modalType === 'createPaper' ? 'Create Paper' : 'Update Paper'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const renderUsersTab = () => (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="mb-0">User Management</h3>
        <button className="btn btn-primary" onClick={handleCreateUser}>
          <i className="fas fa-plus me-2"></i>Create New User
        </button>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : users.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Institution</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.firstName} {user.lastName}</td>
                    <td>{user.email}</td>
                    <td>{user.institution || '-'}</td>
                    <td>
                      <span className={`badge ${user.role === 'ADMIN' ? 'bg-danger' :
                          user.role === 'EDITOR' ? 'bg-warning' :
                            user.role === 'REVIEWER' ? 'bg-info' :
                              'bg-secondary'
                        }`}>
                        {user.role}
                      </span>
                    </td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => handleEditUser(user)}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteUser(user.id)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4">
            <p>No users found. Create your first user.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderPapersTab = () => (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="mb-0">Paper Management</h3>
        <button className="btn btn-primary" onClick={handleCreatePaper}>
          <i className="fas fa-plus me-2"></i>Create New Paper
        </button>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : papers.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Authors</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Volume/Issue</th>
                  <th>Year</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {papers.map(paper => (
                  <tr key={paper.id}>
                    <td>{paper.id}</td>
                    <td className="text-truncate" style={{ maxWidth: '200px' }}>
                      {paper.title}
                    </td>
                    <td className="text-truncate" style={{ maxWidth: '150px' }}>
                      {paper.authors?.map(a => typeof a === 'object' ? a.name : a).join(', ')}
                    </td>
                    <td>{paper.category}</td>
                    <td>
                      <span className={`badge ${paper.status === 'PUBLISHED' ? 'bg-success' :
                          paper.status === 'ACCEPTED' ? 'bg-info' :
                            paper.status === 'REJECTED' ? 'bg-danger' :
                              paper.status === 'UNDER_REVIEW' ? 'bg-primary' :
                                paper.status === 'REVISIONS_REQUIRED' ? 'bg-warning' :
                                  'bg-secondary'
                        }`}>
                        {paper.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>{paper.volume ? `Vol ${paper.volume}, Issue ${paper.issue}` : '-'}</td>
                    <td>{paper.year || '-'}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-info me-1"
                        onClick={() => handleShowCitation(paper)}
                        title="Citations"
                      >
                        <i className="fas fa-quote-right"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary me-1"
                        onClick={() => handleEditPaper(paper)}
                        title="Edit"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeletePaper(paper.id)}
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4">
            <p>No papers found. Create your first paper.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderIssuesTab = () => (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="mb-0">Issues Management</h3>
        <button className="btn btn-primary" onClick={() => {
          setModalType('createIssue');
          setSelectedItem(null);
          setSelectedFile(null);
          setShowModal(true);
        }}>
          <i className="fas fa-plus me-2"></i>Create New Issue
        </button>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : issues.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Volume</th>
                  <th>Issue</th>
                  <th>Year</th>
                  <th>Published</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {issues.map(issue => (
                  <tr key={issue.id}>
                    <td>{issue.id}</td>
                    <td>{issue.title}</td>
                    <td>{issue.volume}</td>
                    <td>{issue.issue}</td>
                    <td>{issue.year}</td>
                    <td>
                      <span className={`badge ${issue.isPublished ? 'bg-success' : 'bg-warning'}`}>
                        {issue.isPublished ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => {
                          setModalType('editIssue');
                          setSelectedItem(issue);
                          setShowModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={async () => {
                          if (window.confirm('Are you sure you want to delete this issue?')) {
                            try {
                              await api.delete(`/admin/issues/${issue.id}`);
                              setIssues(issues.filter(i => i.id !== issue.id));
                              alert('Issue deleted successfully');
                            } catch (err) {
                              alert('Failed to delete issue');
                            }
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">No issues found</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderEditorialTab = () => (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="mb-0">Editorial Board</h3>
        <button className="btn btn-primary" onClick={() => {
          setModalType('createEditorial');
          setSelectedItem(null);
          setShowModal(true);
        }}>
          <i className="fas fa-plus me-2"></i>Add Member
        </button>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : editorialBoard.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Title/Role</th>
                  <th>Institution</th>
                  <th>Department</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {editorialBoard.map(member => (
                  <tr key={member.id}>
                    <td>{member.name}</td>
                    <td>{member.title || member.role}</td>
                    <td>{member.institution}</td>
                    <td>{member.department}</td>
                    <td>{member.email}</td>
                    <td>
                      <span className={`badge ${member.boardType === 'EDITORIAL_TEAM' ? 'bg-primary' : 'bg-info'}`}>
                        {member.boardType === 'EDITORIAL_TEAM' ? 'Team' : 'Board'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => {
                          setModalType('editEditorial');
                          setSelectedItem(member);
                          setShowModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={async () => {
                          if (window.confirm('Are you sure you want to remove this member?')) {
                            try {
                              await api.delete(`/admin/editorial-board/${member.id}`);
                              setEditorialBoard(editorialBoard.filter(m => m.id !== member.id));
                              alert('Member removed successfully');
                            } catch (err) {
                              alert('Failed to remove member');
                            }
                          }
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">No editorial board members found</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderContactTab = () => (
    <div className="card">
      <div className="card-header">
        <h3 className="mb-0">Contact Form Submissions</h3>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : contactForms.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contactForms.map(form => (
                  <tr key={form.id}>
                    <td>{new Date(form.createdAt).toLocaleDateString()}</td>
                    <td>{form.name}</td>
                    <td>{form.email}</td>
                    <td>{form.subject}</td>
                    <td>{form.message.substring(0, 50)}...</td>
                    <td>
                      <span className={`badge ${form.status === 'unread' ? 'bg-warning' : 'bg-success'}`}>
                        {form.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          setModalType('viewContact');
                          setSelectedItem(form);
                          setShowModal(true);
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">No contact form submissions</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderNewsletterTab = () => (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="mb-0">Newsletter Subscriptions</h3>
        <button className="btn btn-success">
          <i className="fas fa-download me-2"></i>Export CSV
        </button>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : newsletterSubs.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Subscribed Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {newsletterSubs.map(sub => (
                  <tr key={sub.id}>
                    <td>{sub.id}</td>
                    <td>{sub.email}</td>
                    <td>{sub.name || '-'}</td>
                    <td>{new Date(sub.createdAt).toLocaleDateString()}</td>
                    <td>
                      <span className={`badge ${sub.isActive ? 'bg-success' : 'bg-secondary'}`}>
                        {sub.isActive ? 'Active' : 'Unsubscribed'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={async () => {
                          if (window.confirm('Remove this subscription?')) {
                            try {
                              await api.delete(`/admin/newsletter-subscriptions/${sub.id}`);
                              setNewsletterSubs(newsletterSubs.filter(s => s.id !== sub.id));
                              alert('Subscription removed');
                            } catch (err) {
                              alert('Failed to remove subscription');
                            }
                          }
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">No newsletter subscriptions</p>
          </div>
        )}
      </div>
    </div>
  );
  const renderAnnouncementsTab = () => (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="mb-0">Announcements Management</h3>
        <button className="btn btn-primary" onClick={() => {
          setModalType('createAnnouncement');
          setSelectedItem(null);
          setShowModal(true);
        }}>
          <i className="fas fa-plus me-2"></i>Add Announcement
        </button>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : announcements.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Priority</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map(announcement => (
                  <tr key={announcement.id}>
                    <td>{announcement.title}</td>
                    <td>{announcement.type}</td>
                    <td>{new Date(announcement.date).toLocaleDateString()}</td>
                    <td>
                      <span className={`badge ${announcement.priority === 'high' ? 'bg-danger' :
                          announcement.priority === 'low' ? 'bg-info' : 'bg-primary'
                        }`}>
                        {announcement.priority}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => {
                          setModalType('editAnnouncement');
                          setSelectedItem(announcement);
                          setShowModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={async () => {
                          if (window.confirm('Delete this announcement?')) {
                            try {
                              await deleteAnnouncement(announcement.id);
                              setAnnouncements(announcements.filter(a => a.id !== announcement.id));
                              alert('Announcement deleted');
                            } catch (err) {
                              alert('Failed to delete announcement');
                            }
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">No announcements found</p>
          </div>
        )}
      </div>
    </div>
  );

  const AnnouncementModal = () => (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {modalType === 'createAnnouncement' ? 'Create Announcement' : 'Edit Announcement'}
            </h5>
            <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
          </div>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const data = Object.fromEntries(formData);

            try {
              if (modalType === 'createAnnouncement') {
                await createAnnouncement(data);
                alert('Announcement created');
              } else {
                await updateAnnouncement(selectedItem.id, data);
                alert('Announcement updated');
              }
              setShowModal(false);
              loadData();
            } catch (err) {
              alert('Failed to save announcement');
            }
          }}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  defaultValue={selectedItem?.title}
                  required
                />
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Type</label>
                  <select className="form-control" name="type" defaultValue={selectedItem?.type || 'General'}>
                    <option value="General">General</option>
                    <option value="Call for Papers">Call for Papers</option>
                    <option value="Deadline">Deadline</option>
                    <option value="Event">Event</option>
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Priority</label>
                  <select className="form-control" name="priority" defaultValue={selectedItem?.priority || 'normal'}>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="date"
                  defaultValue={selectedItem?.date?.split('T')[0] || new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Content</label>
                <textarea
                  className="form-control"
                  name="content"
                  rows="5"
                  defaultValue={selectedItem?.content}
                  required
                ></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Save Announcement</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col">
          <h1>Comprehensive Admin Dashboard</h1>
          <p className="lead">Manage all aspects of the journal system</p>
        </div>
      </div>

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <i className="fas fa-users me-2"></i>Users
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'papers' ? 'active' : ''}`}
            onClick={() => setActiveTab('papers')}
          >
            <i className="fas fa-file-alt me-2"></i>Papers
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'issues' ? 'active' : ''}`}
            onClick={() => setActiveTab('issues')}
          >
            <i className="fas fa-book me-2"></i>Issues
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'editorial' ? 'active' : ''}`}
            onClick={() => setActiveTab('editorial')}
          >
            <i className="fas fa-user-tie me-2"></i>Editorial Board
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            <i className="fas fa-envelope me-2"></i>Contact Forms
            {contactForms.filter(c => c.status === 'unread').length > 0 && (
              <span className="badge bg-danger ms-2">
                {contactForms.filter(c => c.status === 'unread').length}
              </span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'newsletter' ? 'active' : ''}`}
            onClick={() => setActiveTab('newsletter')}
          >
            <i className="fas fa-newspaper me-2"></i>Newsletter
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'announcements' ? 'active' : ''}`}
            onClick={() => setActiveTab('announcements')}
          >
            <i className="fas fa-bullhorn me-2"></i>Announcements
          </button>
        </li>
      </ul>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {activeTab === 'users' && renderUsersTab()}
      {activeTab === 'papers' && renderPapersTab()}
      {activeTab === 'issues' && renderIssuesTab()}
      {activeTab === 'editorial' && renderEditorialTab()}
      {activeTab === 'contact' && renderContactTab()}
      {activeTab === 'newsletter' && renderNewsletterTab()}
      {activeTab === 'announcements' && renderAnnouncementsTab()}

      {showModal && modalType.includes('User') && <UserModal />}
      {showModal && modalType.includes('Paper') && <PaperModal />}
      {showModal && modalType.includes('Issue') && <IssueModal />}
      {showModal && modalType.includes('Editorial') && <EditorialModal />}
      {showModal && modalType.includes('Contact') && <ContactModal />}
      {showModal && modalType.includes('Announcement') && <AnnouncementModal />}

      {showCitationModal && selectedPaperForCitation && (
        <CitationModal
          paper={selectedPaperForCitation}
          onClose={() => setShowCitationModal(false)}
        />
      )}
    </div>
  );
};

export default ComprehensiveAdminDashboard;